import type { LobbyEvent } from '@danda/shared';
import { RoomTypeEnum, LOW_BALANCE_GATE_TEXT, LOW_BALANCE_GATE_BUTTON_LABEL } from '@danda/shared';
import { getStaffAvatarUrl } from '@/lib/media';
import { getBackendVersion } from '@/lib/version';
import { usePmBalanceStore } from '../pmBalanceStore';
import { websocketService } from '@/lib/websocket-service';
import type { EventHandlerContext } from './lobby-event-types';
import { msgId } from './stream-utils';

/**
 * Exact-match error strings that mean "user is out of PM tokens" — backend
 * lobby `dispatch_message` pre-check rejection. We render the Buy Tokens
 * gate bubble (not the in-character fluff) and force pmBalance to 0 so the
 * client-side gate handles further sends locally without round-tripping.
 *
 * De-duped per room to avoid stamping N copies when the client retries.
 */
const INSUFFICIENT_PM_ERROR = 'Insufficient PM tokens. Please add more tokens to continue.';

const NPC_AVATARS: Record<string, string> = {
  tavian: getStaffAvatarUrl('tavian-avatar.webp'),
  aldric: getStaffAvatarUrl('aldric-avatar.webp'),
  kira: getStaffAvatarUrl('kira-avatar.webp'),
};

/**
 * Backend error messages that are transient protocol/state mismatches — NOT real
 * agent failures. Examples: a stale chat session id from a closed Aldric/char-chat
 * session, or a request that arrives before the user_characters map has caught up
 * with a just-completed creation. The active NPC tab is unrelated to these errors,
 * so dressing them up as a "Tavian/Aldric/Kira fallback" message is misleading
 * (it spawned spurious "spectacles flicker" messages right after char creation).
 *
 * Match by exact equality against the backend `send_error(user_id, "...")` strings
 * (not substring) so a future error with similar wording but a real-failure cause
 * isn't accidentally suppressed.
 *
 * NOTE: `Target character is not in the lobby` is intentionally NOT in this set.
 * It also fires from legitimate validation in `handle_char_chat_start` /
 * `handle_char_chat_invite` when a user clicks a stale roster card; that's a real
 * failure the user needs feedback for. Letting it fall through to the
 * in-character fallback at least gives them a visible "something went wrong".
 */
const TRANSIENT_PROTOCOL_ERRORS: ReadonlySet<string> = new Set([
  'Chat session not found',
  "You don't have a character",
  "You don't have a character selected",
]);

export function handleErrorEvent(event: LobbyEvent, ctx: EventHandlerContext): boolean {
  const rawType = (event as { type: string }).type;
  if (rawType !== 'error') return false;

  const errorEvent = event as unknown as { type: 'error'; message: string; code?: string };
  const state = ctx.get();
  const activeRoom = state.chatRooms.find((r) => r.id === state.activeChatRoomId);
  const targetNpc = activeRoom?.type || 'tavian';

  // Log the actual error for debugging, but don't show technical details to users
  console.error(`[backend ${getBackendVersion()}] [LobbyStore] NPC error:`, errorEvent.message);

  // "Chat session not found" for a 1:1 char-chat room means the lobby reaped
  // the backend session while the player was off the lobby WS past the grace
  // window (a duel, a network drop). Transparently revive it by re-issuing
  // char-chat-start so the chat works again instead of falling permanently
  // silent. We deliberately do NOT resend the failed message: that needed a
  // module-global stash that could replay to the wrong NPC after a tab switch
  // or to the next user after a logout. The message is already shown
  // optimistically; the player can resend it once the session is back.
  if (errorEvent.message === 'Chat session not found' && activeRoom?.id.startsWith('char-chat-')) {
    const npcCharId = activeRoom.participants.find((p) => p !== state.selectedCharacterId);
    if (npcCharId) {
      websocketService.startCharacterChat(npcCharId, activeRoom.name);
      state.setWaitingForResponse(false);
      return true;
    }
    // Couldn't resolve the NPC — fall through to the generic swallow below so
    // the spinner at least stops.
  }

  // Swallow transient protocol-level errors so they don't surface as in-character
  // fallback messages. They've already been console.error'd above for debugging.
  if (TRANSIENT_PROTOCOL_ERRORS.has(errorEvent.message)) {
    state.setWaitingForResponse(false);
    return true;
  }

  // Insufficient-PM rejection from the lobby pre-check. Force pmBalance to 0
  // (so subsequent client-side sends gate locally and we stop round-tripping),
  // then render the Buy Tokens gate bubble — but ONLY if the active room
  // doesn't already have a gate bubble as its latest message. Without dedup,
  // a runaway retry loop (e.g. background re-request) would stamp N copies.
  if (errorEvent.message === INSUFFICIENT_PM_ERROR) {
    usePmBalanceStore.getState().setPmBalance(0);
    state.setWaitingForResponse(false);
    // Render the gate in the active room when present; otherwise fall back to
    // 'main' (Tavian's default room) so the user always sees the CTA. Guarded
    // because `addMessage` only buffers party-* rooms — writing to 'main'
    // before it exists (cold connect race window before session-state lands)
    // silently drops the bubble in the .map() that follows.
    const mainRoom = state.chatRooms.find((r) => r.id === 'main');
    const targetRoom = activeRoom ?? mainRoom;
    if (!targetRoom) {
      console.warn(
        '[LobbyStore] Insufficient-PM rejection arrived before chatRooms hydrated; dropping gate bubble.'
      );
      return true;
    }
    const lastMsg = targetRoom.messages[targetRoom.messages.length - 1];
    if (lastMsg?.type === 'token-gate') {
      return true;
    }
    state.addMessage(targetRoom.id, {
      id: msgId('gate'),
      senderId: 'tavian',
      senderName: 'Tavian',
      content: LOW_BALANCE_GATE_TEXT,
      timestamp: new Date(),
      type: 'token-gate',
      avatarUrl: getStaffAvatarUrl('tavian-avatar.webp'),
      meta: {
        action_kind: 'buyTokens',
        action_label: LOW_BALANCE_GATE_BUTTON_LABEL,
      },
    });
    return true;
  }

  let errorContent: string;
  let senderName = 'System';
  let senderId = 'system';
  let messageType: 'aldric' | 'kira' | 'tavian' | 'system' = 'system';
  let avatarUrl: string | undefined;

  if (targetNpc === RoomTypeEnum.ALDRIC) {
    errorContent =
      '*Brother Aldric pauses, his quill hovering over the parchment* ' +
      'Forgive me, traveler. The spirits guiding my hand seem momentarily distant. ' +
      'Please speak again, and we shall continue your sacred journey.';
    senderName = 'Brother Aldric';
    senderId = 'aldric';
    messageType = 'aldric';
    avatarUrl = NPC_AVATARS.aldric;
  } else if (targetNpc === RoomTypeEnum.KIRA) {
    errorContent =
      '*Master Kira lowers her blade* ' +
      'The training grounds grow quiet for a moment. ' +
      'Let us resume when you are ready, warrior.';
    senderName = 'Master Kira';
    senderId = 'kira';
    messageType = 'kira';
    avatarUrl = NPC_AVATARS.kira;
  } else if (targetNpc === RoomTypeEnum.TAVIAN) {
    errorContent =
      "*Tavian raises a hand, steadying a sudden flare in the portal's light* " +
      'The arcane currents twist for a heartbeat, traveler. ' +
      'Speak once more — the way will answer.';
    senderName = 'Tavian';
    senderId = 'tavian';
    messageType = 'tavian';
    avatarUrl = NPC_AVATARS.tavian;
  } else {
    // Active chat is a non-special-NPC room (private 1:1 with a roster NPC,
    // char-chat session, party chat, etc.). Many lobby actions invoked there
    // — `/duel`, party invites, training rejections — return a player-ready
    // narration as the error string. Surfacing it verbatim in Tavian's main
    // chat ("Find someone closer to your skill level..." beats "Something
    // went awry. Please try again.").
    errorContent = errorEvent.message;
  }

  if (state.chatRooms.some((r) => r.id === 'main')) {
    state.addMessage('main', {
      id: msgId('error'),
      senderId,
      senderName,
      content: errorContent,
      timestamp: new Date(),
      type: messageType,
      avatarUrl,
    });
  } else {
    // Same cold-connect race the PM-gate path guards against — `addMessage`
    // only buffers party-* rooms, so writing to 'main' before it exists
    // silently drops the message.
    console.warn(
      '[LobbyStore] WS error arrived before chatRooms hydrated; dropping fallback bubble:',
      errorContent
    );
  }
  state.setWaitingForResponse(false);
  return true;
}
